import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ContactForm from './components/ContactForm';
import OfficeDetails from './components/OfficeDetails';
import FAQSection from './components/FAQSection';
import LiveChatWidget from './components/LiveChatWidget';

const ContactPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mock Zoho CRM integration
  const handleFormSubmission = async (formData) => {
    setIsSubmitting(true);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock CRM data structure
      const crmPayload = {
        lead_source: 'Website Contact Form',
        first_name: formData?.firstName,
        last_name: formData?.lastName,
        email: formData?.email,
        phone: formData?.phone,
        company: formData?.businessType === 'business' ? 'Corporate Client' : 'Individual',
        lead_status: 'New',
        products_interested: formData?.selectedProducts,
        estimated_quantity: formData?.quantity || 1,
        budget_range: formData?.budget,
        preferred_contact_method: formData?.preferredContact,
        additional_requirements: formData?.requirements,
        marketing_consent: formData?.marketingConsent || false,
        terms_accepted: formData?.termsAccepted,
        submission_timestamp: formData?.timestamp,
        lead_score: calculateLeadScore(formData),
        tags: generateLeadTags(formData)
      };

      console.log('CRM Payload:', crmPayload);
      
      // Navigate to thank you page with success state
      navigate('/thank-you-page', { 
        state: { 
          formData: crmPayload,
          submissionSuccess: true 
        } 
      });
      
    } catch (error) {
      console.error('Form submission failed:', error);
      // Handle error state here
      alert('Submission failed. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateLeadScore = (data) => {
    let score = 0;
    
    // Base score for form completion
    score += 20;
    
    // Business type scoring
    if (data?.businessType === 'business') score += 30;
    else score += 15;
    
    // Product interest scoring
    if (data?.selectedProducts?.length > 0) {
      score += data?.selectedProducts?.length * 10;
      if (data?.selectedProducts?.includes('smart-innovation')) score += 15;
      if (data?.selectedProducts?.includes('limited-editions')) score += 10;
    }
    
    // Quantity scoring
    const quantity = parseInt(data?.quantity) || 1;
    if (quantity >= 100) score += 25;
    else if (quantity >= 50) score += 15;
    else if (quantity >= 10) score += 10;
    
    // Budget indication
    if (data?.budget) score += 10;
    
    // Marketing consent
    if (data?.marketingConsent) score += 5;
    
    return Math.min(score, 100); // Cap at 100
  };

  const generateLeadTags = (data) => {
    const tags = ['Website Lead'];
    
    if (data?.businessType === 'business') tags?.push('B2B');
    else tags?.push('B2C');
    
    if (data?.selectedProducts?.includes('smart-innovation')) tags?.push('Tech Enthusiast');
    if (data?.selectedProducts?.includes('limited-editions')) tags?.push('Premium Customer');
    if (parseInt(data?.quantity) >= 50) tags?.push('Bulk Order');
    if (data?.preferredContact === 'whatsapp') tags?.push('WhatsApp Preferred');
    
    return tags;
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - Mava Cold Beverages | Get in Touch</title>
        <meta name="description" content="Contact Mava for premium cold beverage bottles. Get quotes, ask questions, or visit our office. Expert support for personal and corporate hydration needs." />
        <meta name="keywords" content="contact mava, cold beverage bottles, customer support, bulk orders, corporate hydration, premium bottles india" />
        <meta property="og:title" content="Contact Us - Mava Cold Beverages" />
        <meta property="og:description" content="Get in touch with Mava for premium hydration solutions. Expert support for all your cold beverage bottle needs." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://mava.in/contact" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-24 pb-16 bg-gradient-to-br from-primary/5 via-background to-accent/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              <div className="flex items-center justify-center space-x-4 mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center liquid-shadow"
                >
                  <Icon name="MessageSquare" size={32} color="white" />
                </motion.div>
                <div>
                  <h1 className="text-4xl md:text-5xl font-poppins font-bold text-text-primary mb-2">
                    Get in Touch
                  </h1>
                  <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent rounded-full mx-auto" />
                </div>
              </div>
              
              <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
                Ready to elevate your hydration experience? Whether you're looking for personal bottles or corporate solutions, 
                our team is here to help you find the perfect Mava products.
              </p>

              {/* Contact Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12 max-w-4xl mx-auto"
              >
                {[
                  { icon: 'Clock', label: 'Response Time', value: '< 2 Hours', color: 'text-primary' },
                  { icon: 'Users', label: 'Happy Customers', value: '10,000+', color: 'text-accent' },
                  { icon: 'Award', label: 'Satisfaction Rate', value: '98.5%', color: 'text-green-500' }
                ]?.map((stat, index) => (
                  <motion.div
                    key={stat?.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="text-center"
                  >
                    <div className={`w-12 h-12 ${stat?.color?.replace('text-', 'bg-')}/10 rounded-xl flex items-center justify-center mx-auto mb-3`}>
                      <Icon name={stat?.icon} size={24} className={stat?.color} />
                    </div>
                    <h3 className="text-2xl font-poppins font-bold text-text-primary mb-1">
                      {stat?.value}
                    </h3>
                    <p className="text-text-secondary text-sm">
                      {stat?.label}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="mb-8">
                  <h2 className="text-3xl font-poppins font-bold text-text-primary mb-4">
                    Send us an Enquiry
                  </h2>
                  <p className="text-text-secondary">
                    Fill out our detailed form and we'll get back to you with personalized recommendations and pricing.
                  </p>
                </div>
                <ContactForm onSubmit={handleFormSubmission} />
              </motion.div>

              {/* Office Details */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="mb-8">
                  <h2 className="text-3xl font-poppins font-bold text-text-primary mb-4">
                    Visit Our Office
                  </h2>
                  <p className="text-text-secondary">
                    Experience Mava products firsthand at our modern showroom and office space.
                  </p>
                </div>
                <OfficeDetails />
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-surface">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FAQSection />
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-center"
            >
              <h2 className="text-3xl font-poppins font-bold text-text-primary mb-12">
                Trusted by Leading Organizations
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-60">
                {[
                  { name: 'TechCorp', logo: 'Building' },
                  { name: 'GreenEnergy', logo: 'Zap' },
                  { name: 'HealthPlus', logo: 'Heart' },
                  { name: 'EcoSolutions', logo: 'Leaf' }
                ]?.map((company, index) => (
                  <motion.div
                    key={company?.name}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 0.6, scale: 1 }}
                    transition={{ delay: 0.7 + index * 0.1 }}
                    className="flex flex-col items-center space-y-2 hover:opacity-100 brand-transition"
                  >
                    <div className="w-16 h-16 bg-muted rounded-xl flex items-center justify-center">
                      <Icon name={company?.logo} size={32} className="text-text-secondary" />
                    </div>
                    <span className="text-sm font-medium text-text-secondary">
                      {company?.name}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-16 bg-gradient-to-r from-primary to-accent">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="text-white"
            >
              <h2 className="text-3xl font-poppins font-bold mb-4">
                Ready to Start Your Hydration Journey?
              </h2>
              <p className="text-xl opacity-90 mb-8">
                Join thousands of satisfied customers who've made the switch to Mava.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => document.querySelector('.contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-4 bg-white text-primary font-semibold rounded-xl hover:bg-gray-50 brand-transition flex items-center justify-center space-x-2"
                >
                  <Icon name="FileText" size={20} />
                  <span>Get Quote Now</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.open('tel:+918919405132', '_self')}
                  className="px-8 py-4 border-2 border-white text-white font-semibold rounded-xl hover:bg-white hover:text-primary brand-transition flex items-center justify-center space-x-2"
                >
                  <Icon name="Phone" size={20} />
                  <span>Call Now</span>
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Live Chat Widget */}
        <LiveChatWidget />
      </div>
    </>
  );
};

export default ContactPage;